import { updateApplicationVariable } from "../api/controllers/applicationVariables";
import { updateOrganizationVariable } from "../api/controllers/organizationVariables";
import ApplicationVariable from "../models/applications/ApplicationVariable";
import OrganizationVariable from "../models/organizations/OrganizationVariable";
import IResult from "../models/result/IResult";

export type ReferenceKind = "variable" | "prompt" | "constant";
export type ReferenceScope = "Application" | "Organization";

export interface ResolvedReferenceVariable {
    id?: string;
    name: string;
    value: string;
    scope?: ReferenceScope;
    applicationId?: string;
    organizationId?: string;
    isFound: boolean;
}

export interface VariableReference extends ResolvedReferenceVariable {
    start: number;
    end: number;
    text: string;
    kind: ReferenceKind;
}

export const REFERENCE_VARIABLE_UPDATED_EVENT = "voicify-reference-variable-updated";
export const REFERENCE_VARIABLE_PATTERN = /\{\{(variable|prompt|constant)\.([A-Za-z0-9_-]+)\}\}/g;

export const getResolvedReferenceVariablesByName = (
    applicationVariables: ApplicationVariable[],
    organizationVariables: OrganizationVariable[]
): Record<string, ResolvedReferenceVariable> => {
    const variables: Record<string, ResolvedReferenceVariable> = {};

    organizationVariables.forEach(variable => {
        variables[variable.name] = {
            id: variable.id,
            name: variable.name,
            value: variable.value || "",
            scope: "Organization",
            organizationId: variable.organizationId,
            isFound: true
        };
    });

    applicationVariables.forEach(variable => {
        variables[variable.name] = {
            id: variable.id,
            name: variable.name,
            value: variable.value || "",
            scope: "Application",
            applicationId: variable.applicationId,
            isFound: true
        };
    });

    return variables;
};

export const findVariableReferences = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>
): VariableReference[] => {
    const references: VariableReference[] = [];
    const regex = new RegExp(REFERENCE_VARIABLE_PATTERN.source, "g");
    let match: RegExpExecArray;

    while ((match = regex.exec(text)) !== null) {
        const variable = variablesByName[match[2]];
        references.push({
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            kind: match[1] as ReferenceKind,
            name: match[2],
            value: variable?.value || "",
            id: variable?.id,
            scope: variable?.scope,
            applicationId: variable?.applicationId,
            organizationId: variable?.organizationId,
            isFound: !!variable?.isFound
        });
    }

    return references;
};

export const resolveReferenceVariables = (
    text: string,
    variablesByName: Record<string, ResolvedReferenceVariable>,
    maxDepth: number = 10
): string => {
    if (maxDepth <= 0) {
        return text;
    }

    let replacedReference = false;
    const regex = new RegExp(REFERENCE_VARIABLE_PATTERN.source, "g");
    const resolved = text.replace(regex, (match, _kind: ReferenceKind, name: string) => {
        const variable = variablesByName[name];
        if (!variable?.isFound) {
            return match;
        }

        replacedReference = true;
        return variable.value || "";
    });

    return replacedReference && resolved !== text
        ? resolveReferenceVariables(resolved, variablesByName, maxDepth - 1)
        : resolved;
};

export const unwrapVariableReference = (
    text: string,
    reference: Pick<VariableReference, "start" | "end" | "text" | "value">
): string => {
    if (text.substring(reference.start, reference.end) !== reference.text) {
        return text;
    }

    return `${text.substring(0, reference.start)}${reference.value}${text.substring(reference.end)}`;
};

const invalidResult = (message: string): IResult<ResolvedReferenceVariable> => ({
    resultType: "Invalid",
    data: null,
    errors: [message]
});

export const updateResolvedReferenceVariable = async (
    variable: ResolvedReferenceVariable,
    value: string
): Promise<IResult<ResolvedReferenceVariable>> => {
    if (!variable.id || !variable.scope) {
        return invalidResult("This reference is missing the information required to update it");
    }

    if (variable.scope === "Application") {
        if (!variable.applicationId) {
            return invalidResult("This reference is missing its application");
        }

        const result = await updateApplicationVariable(variable.id, {
            id: variable.id,
            name: variable.name,
            value,
            applicationId: variable.applicationId
        });
        return result.resultType === "Ok"
            ? {
                  resultType: "Ok",
                  data: {
                      ...variable,
                      value: result.data?.value === undefined ? value : result.data.value
                  },
                  errors: []
              }
            : invalidResult(result.errors?.[0] || "Could not update the application reference");
    }

    if (!variable.organizationId) {
        return invalidResult("This reference is missing its organization");
    }

    const result = await updateOrganizationVariable(variable.id, {
        id: variable.id,
        name: variable.name,
        value,
        organizationId: variable.organizationId
    });
    return result.resultType === "Ok"
        ? {
              resultType: "Ok",
              data: {
                  ...variable,
                  value: result.data?.value === undefined ? value : result.data.value
              },
              errors: []
          }
        : invalidResult(result.errors?.[0] || "Could not update the organization reference");
};

export const notifyReferenceVariableUpdated = (variable: ResolvedReferenceVariable): void => {
    if (typeof window === "undefined") {
        return;
    }

    window.dispatchEvent(new CustomEvent<ResolvedReferenceVariable>(REFERENCE_VARIABLE_UPDATED_EVENT, {
        detail: variable
    }));
};
